import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, AlertCircle, User, Flag } from 'lucide-react';
import { format, addMinutes, addHours, addDays, isBefore } from 'date-fns';

const TaskCreationModal = ({ isOpen, onClose, onCreate, initialTitle = '', agents = [], chatPartner = null }) => {
    const [title, setTitle] = useState(initialTitle);
    const [description, setDescription] = useState('');
    const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [time, setTime] = useState(format(addHours(new Date(), 1), 'HH:mm'));
    const [priority, setPriority] = useState('medium');
    const [reminder, setReminder] = useState('15m');
    const [assignedTo, setAssignedTo] = useState('self');

    useEffect(() => {
        if (isOpen) {
            setTitle(initialTitle);
            setDescription('');
            setDate(format(new Date(), 'yyyy-MM-dd'));
            setTime(format(addHours(new Date(), 1), 'HH:mm'));
            setPriority('medium');
            // Default to chat partner if available, else self
            if (chatPartner) {
                setAssignedTo(chatPartner.id);
            } else {
                setAssignedTo('self');
            }
        }
    }, [isOpen, initialTitle, chatPartner]);

    if (!isOpen) return null;

    const handleSubmit = () => {
        if (!title.trim()) {
            alert("Please enter a task title.");
            return;
        }

        const dueDateTime = new Date(`${date}T${time}`);
        if (isBefore(dueDateTime, new Date())) {
            alert("Please select a future date and time.");
            return;
        }

        // Calculate reminder timestamp
        let reminderTime = new Date(dueDateTime);
        switch (reminder) {
            case '5m': reminderTime = addMinutes(reminderTime, -5); break;
            case '10m': reminderTime = addMinutes(reminderTime, -10); break;
            case '15m': reminderTime = addMinutes(reminderTime, -15); break;
            case '30m': reminderTime = addMinutes(reminderTime, -30); break;
            case '1h': reminderTime = addHours(reminderTime, -1); break;
            case '1d': reminderTime = addDays(reminderTime, -1); break;
            default: reminderTime = null;
        }

        onCreate({
            title,
            description,
            dueTimestamp: dueDateTime.getTime(),
            reminderTimestamp: reminderTime ? reminderTime.getTime() : null,
            priority,
            assignedTo
        });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden border border-slate-100 scale-100 animate-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="font-bold text-lg text-slate-800">Create New Task</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 hover:bg-slate-200 p-1 rounded-full transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 space-y-5">
                    {/* Title */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Task Title</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            placeholder="What needs to be done?"
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all font-medium text-slate-700"
                            autoFocus
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Description (Optional)</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Add details..."
                            rows={2}
                            className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all resize-none"
                        />
                    </div>

                    {/* Date & Time Row */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Due Date</label>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-2.5 text-slate-400" size={16} />
                                <input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                    className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-500 transition-all"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Due Time</label>
                            <div className="relative">
                                <Clock className="absolute left-3 top-2.5 text-slate-400" size={16} />
                                <input
                                    type="time"
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                    className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-500 transition-all"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Priority & Reminder Row */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Priority</label>
                            <div className="relative">
                                <Flag className={`absolute left-3 top-2.5 ${priority === 'high' ? 'text-red-500' : priority === 'medium' ? 'text-yellow-500' : 'text-blue-500'}`} size={16} />
                                <select
                                    value={priority}
                                    onChange={(e) => setPriority(e.target.value)}
                                    className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-500 appearance-none cursor-pointer"
                                >
                                    <option value="low">Low Priority</option>
                                    <option value="medium">Medium Priority</option>
                                    <option value="high">High Priority</option>
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Reminder</label>
                            <div className="relative">
                                <AlertCircle className="absolute left-3 top-2.5 text-slate-400" size={16} />
                                <select
                                    value={reminder}
                                    onChange={(e) => setReminder(e.target.value)}
                                    className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-500 appearance-none cursor-pointer"
                                >
                                    <option value="">No Reminder</option>
                                    <option value="5m">5 mins before</option>
                                    <option value="15m">15 mins before</option>
                                    <option value="30m">30 mins before</option>
                                    <option value="1h">1 hour before</option>
                                    <option value="1d">1 day before</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Assigned To */}
                    <div>
                        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Assign To</label>
                        <div className="relative">
                            <User className="absolute left-3 top-2.5 text-slate-400" size={16} />
                            <select
                                value={assignedTo}
                                onChange={(e) => setAssignedTo(e.target.value)}
                                className="w-full pl-10 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-purple-500 appearance-none cursor-pointer"
                            >
                                <option value="self">Myself</option>
                                {chatPartner && (
                                    <option value={chatPartner.id}>
                                        {chatPartner.name || 'Chat Partner'}
                                    </option>
                                )}
                                {agents && agents
                                    .filter(a => !chatPartner || a.id !== chatPartner.id) // Avoid duplicate if partner is agent
                                    .map(agent => (
                                        <option key={agent.id} value={agent.id}>
                                            {agent.name || agent.email}
                                        </option>
                                    ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-slate-50 px-6 py-4 border-t border-slate-100 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 text-slate-600 font-medium hover:bg-slate-200 rounded-xl transition-colors text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        className="px-6 py-2.5 bg-purple-600 text-white font-bold rounded-xl hover:bg-purple-700 shadow-lg shadow-purple-200 transition-all transform active:scale-95 text-sm flex items-center gap-2"
                    >
                        Create Task
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TaskCreationModal;
